import React from "react";
import {
  Image,
  Text,
  View,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  ToastAndroid,
} from "react-native";

import { Formik } from "formik";
import * as Yup from "yup";
import { AuthContext } from "../../contexts/auth/Store";

import { AnalyticsService } from "../../constants/Analytics";
import ConfigService from "../../services/ConfigService";

import FormikInput from "../../components/form/FormikInput";
import FormikSelectList from "../../components/form/FormikSelectList";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useNavigation, useRoute } from "@react-navigation/native";
import Colors from "../../constants/Colors";
import StyledButton from "../../components/StyledButton";
import GenericHeader from "../../components/headers/GenericHeader";
import FontStyles from "../../constants/FontStyle";
import { CartContext } from "../../contexts/cart/Store";
import { ProductContext } from "../../contexts/product/Store";
import { OrderContext } from "../../contexts/order/Store";
import { HistoryContext } from "../../contexts/history/Store";
import http from "../../utils/http";
import DeviceTokenService from "../../services/DeviceTokenService";

const analytics = new AnalyticsService();
const configService = new ConfigService();
const deviceTokenService = new DeviceTokenService();

const SignupSchema = Yup.object().shape({
  phone: Yup.string()
    .matches(/^[0][7-9][0-1][0-9]{8}$/, "Phone number is invalid")
    .required("Enter your phone number"),
  name: Yup.string().required("Enter your store name"),
  location_id: Yup.string().required("Enter your store location"),
  address: Yup.string().required("Enter your store address"),
});

function OnboardScreen() {
  const navigation = useNavigation();
  const route = useRoute();

  const authenticator = React.useContext(AuthContext);
  const [cart_state, cart_dispatch, cart_fetch] = React.useContext(CartContext);
  const { product_state, product_dispatch, product_fetch } =
    React.useContext(ProductContext);
  const { dispatch: order_dispatch, action: order_fetch } =
    React.useContext(OrderContext);
  const [history_state, history_dispatch, history_fetch] =
    React.useContext(HistoryContext);

  const gotoAuth = () => {
    navigation.navigate("Auth");
  };

  const [store_type, setStoreType] = React.useState([]);
  const [locations, setLocations] = React.useState([]);
  const [phone, setPhone] = React.useState("");

  const _createAccount = async (values, setSubmitting) => {
    let form = new FormData();
    form.append("phone", values.phone);
    form.append("name", values.name);
    form.append("person", "");
    form.append("address", values.address);
    form.append("location_id", values.location_id);
    form.append("store_type_id", values.store_type_id);
    form.append("status", "active");
    form.append("hash", "password");

    http
      .post("buyer/signup", form)
      .then((res) => {
        if (res.code === 200) {
          form = new FormData();
          form.append("phone", values.phone);
          form.append("remember", "true");
          http
            .post("buyer/sesame", form)
            .then((res) => {
              if (res.code === 200 && res.message === true && res.data) {
                let buyer = res.data;
                form = new FormData();
                form.append("_id", buyer._id);
                http
                  .post("buyer/read_details", form, buyer.token)
                  .then(async (details_res) => {
                    // setSubmitting(false)
                    const authObj = Object.assign({}, buyer, {
                      buyerData: details_res.data,
                    });
                    // authenticator.signIn(authObj)
                    analytics.mixpanelSetUser(
                      details_res.data._id,
                      Object.assign({}, details_res.data, {
                        $phone: details_res.data.phone,
                        $created: new Date(),
                      })
                    );
                    analytics.googleHelpers.setUserId(details_res.data._id);

                    setTimeout(() => {
                      analytics.logEvent("Signed Up", details_res.data);
                      analytics.logEvent("Logged In", details_res.data);
                    }, 2500);

                    try {
                      await AsyncStorage.setItem("userToken", authObj.token);
                      await AsyncStorage.setItem("buyerId", authObj._id);
                      await AsyncStorage.setItem(
                        "buyerData",
                        JSON.stringify(authObj.buyerData)
                      );

                      // get/save grid settings to local storage
                      await configService.fetchAppConfig();

                      // get/save FCM token
                      await deviceTokenService.fetchFcmToken();

                      // clear any pre-existing data in state
                      cart_dispatch({ type: "CLEAR_ALL" });
                      order_dispatch({ type: "CLEAR_ALL" });
                      product_dispatch({ type: "CLEAR_ALL" });
                      history_dispatch({ type: "CLEAR_ALL" });

                      Promise.all([
                        cart_fetch(),
                        product_fetch(),
                        order_fetch(),
                        history_fetch(),
                      ])
                        .then((res) => {
                          authenticator.signIn(authObj);
                        })
                        .catch((err) => {
                          // ToastAndroid.show(err?.message, ToastAndroid.SHORT, ToastAndroid.BOTTOM)
                        });
                    } catch (err) {
                      // ToastAndroid.show(err?.message, ToastAndroid.SHORT, ToastAndroid.BOTTOM)
                    }
                  })
                  .catch((err) => {
                    setSubmitting(false);
                  });
              } else {
                navigation.navigate("Onboard", { phone: values.phone }); // goto signup afresh
                setSubmitting(false);
              }
            })
            .catch((err) => {
              // ToastAndroid.show(err?.message || 'An error occured. Try again', ToastAndroid.SHORT, ToastAndroid.BOTTOM)
              setSubmitting(false);
            });
        } else {
          // ToastAndroid.show(res?.message || 'An error occured. Try again', ToastAndroid.SHORT, ToastAndroid.BOTTOM)
          setSubmitting(false);
        }
      })
      .catch((err) => {
        // ToastAndroid.show(err?.message || 'An error occured. Try again', ToastAndroid.SHORT, ToastAndroid.BOTTOM)
        setSubmitting(false);
      })

      .catch((err) => {
        // ToastAndroid.show(err?.message || 'An error occured. Try again', ToastAndroid.SHORT, ToastAndroid.BOTTOM)
        setSubmitting(false);
      });
  };

  React.useEffect(() => {
    const loadStoreTypes = async () => {
      const store_types = await AsyncStorage.getItem("storeTypes");
      const store_locations = await AsyncStorage.getItem("storeLocations");

      setStoreType(JSON.parse(store_types));
      setLocations(JSON.parse(store_locations));

      http.post("store_type/read_all", null, null).then((data) => {
        let list = data.data.filter((item) => {
          return item.status === "active";
        });
        AsyncStorage.setItem("storeTypes", JSON.stringify(list));
        setStoreType(list);
      });

      http.post("location/read_all", null, null).then((data) => {
        let list = data.data.filter((item) => {
          return item.status === "active";
        });

        AsyncStorage.setItem("storeLocations", JSON.stringify(list));
        setLocations(list);
      });
    };

    loadStoreTypes();

    if (navigation.isFocused()) {
      if (route?.params?.phone) {
        setPhone(route.params.phone);
      } else {
        navigation.navigate("Auth");
      }
    }
  }, [navigation]);

  return (
    <View style={styles.container}>
      <GenericHeader
        title={route?.params?.phone || "Setup your account"}
        backAction={() => navigation.navigate("Auth")}
      />
      <KeyboardAvoidingView
        style={{ flex: 1, justifyContent: "flex-start" }}
        keyboardVerticalOffset={0}
        enabled={true}
        behavior={"height"}
      >
        <ScrollView contentContainerStyle={styles.scrollView}>
          <View>
            <Text style={styles.headerText}>Setup your account</Text>
            <Text style={styles.subHeaderText}>
              We’ll need a few details about your store to proceed.
            </Text>

            {phone !== "" && (
              <Formik
                initialValues={{
                  name: "",
                  phone,
                  person: "",
                  address: "",
                  store_type_id: "",
                  location_id: "",
                  reference: "",
                  hash: "",
                  status: "active",
                }}
                enableReinitialize={true}
                validationSchema={SignupSchema}
                onSubmit={(values, actions) => {
                  actions.setSubmitting(true);
                  _createAccount(values, actions.setSubmitting);
                }}
              >
                {({ handleSubmit, isSubmitting, isValid }) => (
                  <>
                    {/* form goes here */}
                    <FormikInput
                      fieldName="name"
                      label="Store Name"
                      placeholder="Enter store name"
                    />

                    <FormikSelectList
                      fieldName="location_id"
                      placeholder="Select store location"
                      label="Store Location"
                      options={locations}
                    />

                    <FormikSelectList
                      fieldName="store_type_id"
                      placeholder="Select store type"
                      label="Store Type"
                      options={store_type}
                    />

                    <FormikInput
                      fieldName="address"
                      label="Store Address"
                      placeholder="Enter delivery address"
                      numberOfLines={3}
                    />

                    <StyledButton
                      label="Continue"
                      submitting={isSubmitting}
                      disabled={isSubmitting || !isValid}
                      action={handleSubmit}
                      style={{
                        marginTop: 40,
                      }}
                    />
                  </>
                )}
              </Formik>
            )}
          </View>
          <View style={styles.footerContainer}>
            <Image
              source={require("../../assets/images/main/nav-logo.png")}
              style={styles.image}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "flex-start",
  },
  scrollView: {
    paddingHorizontal: 25,
    backgroundColor: Colors.whiteColor,
    // flex: 1,
    flexGrow: 1,
    justifyContent: "space-between",
  },
  image: {
    width: 96,
    height: 27.26,
    alignSelf: "center",
    marginTop: 40,
  },
  headerText: {
    alignSelf: "flex-start",
    color: "#171C34",
    ...FontStyles.size22,
    ...FontStyles.lh26,
    ...FontStyles.bold,
    marginTop: 35,
  },
  subHeaderText: {
    color: "#C4CAD0",
    ...FontStyles.size16,
    ...FontStyles.lh22,
    ...FontStyles.medium,
    alignSelf: "flex-start",
    marginTop: 6,
    marginBottom: 44,
  },
  footerContainer: {
    paddingBottom: 22,
  },
});

export default OnboardScreen;
